import React, { Component } from "react";
import axios from "axios";
import Helmet from "react-helmet";
import { apiHost } from "../../api/apiClient";
import { utcToLocal } from "../../utils/dateConvert";
import "../../styles/pageVisits/pageVisits.scss";

class PageVisits extends Component {
  constructor(props) {
    super(props);
    const heatMapOnly = props.heatMapOnly === true;
    this.state = {
      visits: [],
      showAllVisits: false,
      showHeatMap: heatMapOnly,
      excludeSystemAdminFromHeatMap: true,
      selectedHeatMapPage: null,
      highlightedUserKey: "",
      searchText: "",
      isLoading: false,
      error: "",
    };
  }

  componentDidMount() {
    this.loadVisits();
  }

  getUnexpectedResponseMessage(response) {
    const contentType = response && response.headers ? response.headers["content-type"] || "" : "";
    const status = response ? response.status : "";
    const data = response ? response.data : null;

    if (typeof data === "string" && data.trim().startsWith("<!doctype")) {
      return `Page visits API returned the site HTML instead of data. Check that the updated backend DLL is deployed and the app has been restarted. Status: ${status}.`;
    }

    if (typeof data === "string" && data.trim().startsWith("<html")) {
      return `Page visits API returned the site HTML instead of data. Check that the updated backend DLL is deployed and the app has been restarted. Status: ${status}.`;
    }

    if (data && typeof data === "object") {
      return `Page visits API returned ${Object.prototype.toString.call(data)} instead of a list. Status: ${status}.`;
    }

    return `Page visits API returned an unexpected response. Status: ${status}. Content-Type: ${contentType || "unknown"}.`;
  }

  loadVisits = async () => {
    this.setState({ isLoading: true, error: "" });

    try {
      const response = await axios.post(
        `${apiHost}/api/PageVisit/Recent`,
        { limit: 5000, sinceHours: 24 },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      const visits = Array.isArray(response.data) ? response.data : [];
      const error = Array.isArray(response.data)
        ? ""
        : this.getUnexpectedResponseMessage(response);

      this.setState({ visits, isLoading: false, error });
    } catch (error) {
      const status = error && error.response ? error.response.status : "";
      const detail = status ? ` Status: ${status}.` : "";
      const responseText =
        error && error.response && error.response.data
          ? ` ${typeof error.response.data === "string" ? error.response.data : JSON.stringify(error.response.data)}`
          : "";

      this.setState({
        visits: [],
        isLoading: false,
        error: `Unable to load page visits.${detail}${responseText}`,
      });
    }
  };

  renderDate(value) {
    if (!value) {
      return "";
    }

    return utcToLocal(value);
  }

  renderPath(visit) {
    return `${visit.path || ""}${visit.queryString || ""}`;
  }

  getPageVisitUrl(visit) {
    if (!visit || !visit.path) {
      return "";
    }

    const path = visit.path.toString().trim();
    if (!path || path.indexOf("//") === 0 || /^https?:\/\//i.test(path)) {
      return "";
    }

    const queryString = visit.queryString || "";
    const normalisedPath = path.charAt(0) === "/" ? path : `/${path}`;

    return `${normalisedPath}${queryString}`;
  }

  renderPageLink(visit) {
    const path = this.renderPath(visit);
    const url = this.getPageVisitUrl(visit);

    return (
      <div className="page-visits-path-cell">
        <span>{path}</span>
        {url && (
          <a
            className="page-visits-link-button"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open page in a new tab"
          >
            View
          </a>
        )}
      </div>
    );
  }

  getRoleBadgeLabel(role) {
    const roleValue = role || "User";
    const labels = {
      CommunityManager: "Community Manager",
      "Community Manager": "Community Manager",
      VTDemo: "VtDemo",
      VtDemo: "VtDemo",
      SystemAdmin: "SystemAdmin",
      User: "User",
    };

    return labels[roleValue] || roleValue;
  }

  getRoleBadgeClass(role) {
    const normalisedRole = (role || "User").toString().toLowerCase().replace(/\s+/g, "");
    const classMap = {
      systemadmin: "systemadmin",
      communitymanager: "communitymanager",
      user: "user",
      vtdemo: "vtdemo",
    };

    return classMap[normalisedRole] || "default";
  }

  renderRoleBadge(role) {
    return (
      <span className={`vt-role-badge vt-role-badge-${this.getRoleBadgeClass(role)}`}>
        {this.getRoleBadgeLabel(role)}
      </span>
    );
  }

  renderCustomer(visit) {
    if (!visit) {
      return "";
    }

    return visit.customerName || visit.customerId || "";
  }

  getVisitTime(visit) {
    const value = visit && visit.visitStartedUtc ? new Date(visit.visitStartedUtc).getTime() : 0;
    return Number.isNaN(value) ? 0 : value;
  }

  getUserKey(visit) {
    return visit.userId || visit.userName || "unknown-user";
  }

  handleSearchChange = (event) => {
    this.setState({
      searchText: event.target.value,
      highlightedUserKey: "",
      selectedHeatMapPage: null,
    });
  };

  getFilteredVisits(visits) {
    const searchText = (this.state.searchText || "").trim().toLowerCase();
    if (!searchText) {
      return visits;
    }

    const terms = searchText.split(/\s+/).filter(Boolean);
    return visits.filter((visit) => {
      const searchableText = [
        visit.userName,
        visit.userId,
        visit.role,
        visit.customerName,
        visit.customerId,
        visit.path,
        visit.queryString,
        visit.pageTitle,
        visit.ipAddress,
        visit.userAgent,
        visit.clientTimezone,
        visit.visitStartedUtc,
      ].filter(Boolean).join(" ").toLowerCase();

      return terms.every((term) => searchableText.indexOf(term) !== -1);
    });
  }

  renderSearchInput() {
    return (
      <label className="page-visits-search">
        <i className="fa fa-search" aria-hidden="true"></i>
        <input
          aria-label="Search page visits"
          onChange={this.handleSearchChange}
          placeholder="Search users, customers, paths..."
          type="search"
          value={this.state.searchText}
        />
      </label>
    );
  }

  getUserSummaries(visits) {
    const groups = {};

    visits.forEach((visit) => {
      const userKey = this.getUserKey(visit);
      if (!groups[userKey]) {
        groups[userKey] = {
          userKey,
          userName: visit.userName || "Unknown user",
          userId: visit.userId || "",
          role: visit.role || "",
          customerName: visit.customerName || "",
          customerId: visit.customerId || "",
          userAgent: visit.userAgent || "",
          visits: [],
          pages: {},
          ips: {},
          customers: {},
        };
      }

      groups[userKey].visits.push(visit);
      if (visit.path) {
        groups[userKey].pages[visit.path] = true;
      }
      if (visit.ipAddress) {
        groups[userKey].ips[visit.ipAddress] = true;
      }
      if (visit.customerName || visit.customerId) {
        groups[userKey].customers[visit.customerName || visit.customerId] = true;
      }
    });

    return Object.keys(groups)
      .map((key) => {
        const summary = groups[key];
        summary.visits.sort((a, b) => this.getVisitTime(b) - this.getVisitTime(a));
        summary.latestVisit = summary.visits[0] || {};
        summary.pageCount = Object.keys(summary.pages).length;
        summary.ipList = Object.keys(summary.ips);
        summary.customerList = Object.keys(summary.customers);
        summary.customerName = summary.latestVisit.customerName || summary.customerName;
        summary.customerId = summary.latestVisit.customerId || summary.customerId;
        summary.userAgent = summary.latestVisit.userAgent || summary.userAgent;
        return summary;
      })
      .sort((a, b) => this.getVisitTime(b.latestVisit) - this.getVisitTime(a.latestVisit));
  }

  showUserDetail = (userKey) => {
    this.props.history.push(`/page-visits/user/${encodeURIComponent(userKey)}`);
  };

  showSummary = () => {
    this.props.history.push("/page-visits");
  };

  toggleAllVisits = () => {
    this.setState((state) => ({
      showAllVisits: !state.showAllVisits,
      showHeatMap: false,
      highlightedUserKey: "",
    }));
  };

  showAllLogins = () => {
    this.setState({ showAllVisits: true, showHeatMap: false, highlightedUserKey: "", selectedHeatMapPage: null });
  };

  showHeatMap = () => {
    this.setState({ showAllVisits: false, showHeatMap: true, highlightedUserKey: "" });
  };

  showUserSummary = () => {
    this.setState({ showAllVisits: false, showHeatMap: false, highlightedUserKey: "", selectedHeatMapPage: null });
  };

  toggleHighlightedUser = (userKey) => {
    this.setState((state) => ({
      highlightedUserKey: state.highlightedUserKey === userKey ? "" : userKey,
    }));
  };

  toggleHeatMapSystemAdmin = () => {
    this.setState((state) => ({
      excludeSystemAdminFromHeatMap: !state.excludeSystemAdminFromHeatMap,
      selectedHeatMapPage: null,
    }));
  };

  showHeatMapPageUsers = (page) => {
    this.setState({ selectedHeatMapPage: page });
  };

  closeHeatMapPageUsers = () => {
    this.setState({ selectedHeatMapPage: null });
  };

  renderIpList(ips) {
    if (!ips || ips.length === 0) {
      return "";
    }

    return ips.join(", ");
  }

  renderUserAgent(value) {
    if (!value) {
      return "";
    }

    return value;
  }

  getHeatMapVisits(visits) {
    return visits.filter((visit) => {
      const pageTitle = visit.pageTitle || "No PageTitle";
      const isPageVisitsReport = pageTitle.indexOf("Page Visits") === 0;
      const isExcludedRole = this.state.excludeSystemAdminFromHeatMap && visit.role === "SystemAdmin";

      return !isPageVisitsReport && !isExcludedRole;
    });
  }

  getPageHeatMapItems(visits) {
    const groups = {};

    visits.forEach((visit) => {
      const pageKey = visit.pageTitle || "No PageTitle";
      if (!groups[pageKey]) {
        groups[pageKey] = {
          pageKey,
          pageTitle: pageKey,
          visits: 0,
          users: {},
          userVisits: {},
          latestVisit: visit,
        };
      }

      groups[pageKey].visits += 1;
      groups[pageKey].latestVisit = this.getVisitTime(visit) > this.getVisitTime(groups[pageKey].latestVisit)
        ? visit
        : groups[pageKey].latestVisit;

      const userKey = this.getUserKey(visit);
      if (userKey) {
        groups[pageKey].users[userKey] = true;
        if (!groups[pageKey].userVisits[userKey]) {
          groups[pageKey].userVisits[userKey] = {
            userKey,
            userName: visit.userName || "Unknown user",
            role: visit.role || "",
            customerName: visit.customerName || "",
            customerId: visit.customerId || "",
            visits: 0,
            paths: {},
            latestVisit: visit,
          };
        }

        groups[pageKey].userVisits[userKey].visits += 1;
        const visitPath = this.renderPath(visit) || "Unknown path";
        groups[pageKey].userVisits[userKey].paths[visitPath] = (groups[pageKey].userVisits[userKey].paths[visitPath] || 0) + 1;
        groups[pageKey].userVisits[userKey].latestVisit = this.getVisitTime(visit) > this.getVisitTime(groups[pageKey].userVisits[userKey].latestVisit)
          ? visit
          : groups[pageKey].userVisits[userKey].latestVisit;
        groups[pageKey].userVisits[userKey].role = groups[pageKey].userVisits[userKey].latestVisit.role || groups[pageKey].userVisits[userKey].role;
        groups[pageKey].userVisits[userKey].customerName = groups[pageKey].userVisits[userKey].latestVisit.customerName || groups[pageKey].userVisits[userKey].customerName;
        groups[pageKey].userVisits[userKey].customerId = groups[pageKey].userVisits[userKey].latestVisit.customerId || groups[pageKey].userVisits[userKey].customerId;
      }
    });

    return Object.keys(groups)
      .map((key) => {
        const item = groups[key];
        item.userCount = Object.keys(item.users).length;
        item.userRows = Object.keys(item.userVisits)
          .map((userKey) => {
            const user = item.userVisits[userKey];
            user.pathRows = Object.keys(user.paths)
              .map((path) => ({ path, visits: user.paths[path] }))
              .sort((a, b) => b.visits - a.visits || a.path.localeCompare(b.path));
            return user;
          })
          .sort((a, b) => b.visits - a.visits || this.getVisitTime(b.latestVisit) - this.getVisitTime(a.latestVisit));
        return item;
      })
      .sort((a, b) => b.visits - a.visits);
  }

  getHeatMapLayout(items, x = 0, y = 0, width = 100, height = 100) {
    if (!items || items.length === 0) {
      return [];
    }

    if (items.length === 1) {
      return [{ ...items[0], x, y, width, height }];
    }

    const totalVisits = items.reduce((sum, item) => sum + item.visits, 0);
    let runningVisits = 0;
    let splitIndex = 0;

    for (let index = 0; index < items.length; index += 1) {
      const nextRunningVisits = runningVisits + items[index].visits;
      if (index > 0 && Math.abs((totalVisits / 2) - runningVisits) < Math.abs((totalVisits / 2) - nextRunningVisits)) {
        break;
      }

      runningVisits = nextRunningVisits;
      splitIndex = index + 1;
    }

    splitIndex = Math.max(1, Math.min(splitIndex, items.length - 1));

    const firstItems = items.slice(0, splitIndex);
    const secondItems = items.slice(splitIndex);
    const firstVisits = firstItems.reduce((sum, item) => sum + item.visits, 0);
    const firstRatio = totalVisits > 0 ? firstVisits / totalVisits : 0.5;

    if (width >= height) {
      const firstWidth = width * firstRatio;
      return [
        ...this.getHeatMapLayout(firstItems, x, y, firstWidth, height),
        ...this.getHeatMapLayout(secondItems, x + firstWidth, y, width - firstWidth, height),
      ];
    }

    const firstHeight = height * firstRatio;
    return [
      ...this.getHeatMapLayout(firstItems, x, y, width, firstHeight),
      ...this.getHeatMapLayout(secondItems, x, y + firstHeight, width, height - firstHeight),
    ];
  }

  renderHeatMap(visits) {
    const heatMapVisits = this.getHeatMapVisits(visits);
    const pages = this.getPageHeatMapItems(heatMapVisits);
    const layout = this.getHeatMapLayout(pages);
    const colorClasses = [
      "page-visits-heat-tile-blue",
      "page-visits-heat-tile-green",
      "page-visits-heat-tile-teal",
      "page-visits-heat-tile-indigo",
      "page-visits-heat-tile-amber",
      "page-visits-heat-tile-slate",
    ];

    if (!this.state.isLoading && pages.length === 0) {
      return (
        <div className="page-visits-table-shell">
          <div className="page-visits-empty">No page visits found in the last 24 hours.</div>
        </div>
      );
    }

    return (
      <div className="page-visits-heatmap-shell">
        {layout.map((page, index) => {
          const label = page.pageTitle;
          const percent = visits.length > 0 ? Math.round((page.visits / visits.length) * 100) : 0;
          const area = page.width * page.height;
          const compactClass = area < 70 ? " page-visits-heat-tile-compact" : "";
          const tinyClass = area < 34 ? " page-visits-heat-tile-tiny" : "";
          const isInline = page.width > page.height * 1.2 || page.height < 16 || area < 70;
          const inlineClass = isInline ? " page-visits-heat-tile-inline" : "";
          const titlePriorityClass = page.height < 19 || area < 115 ? " page-visits-heat-tile-title-priority" : "";
          const shortestSide = Math.min(page.width, page.height);
          let labelSize = Math.max(10, Math.min(42, shortestSide * 1.55, Math.sqrt(area) * 2.45));
          let countSize = Math.max(12, Math.min(72, shortestSide * 2.4, Math.sqrt(area) * 3.4));

          if (isInline) {
            labelSize = Math.max(10, Math.min(26, page.height * 2.25, Math.sqrt(area) * 1.65));
            countSize = Math.max(12, Math.min(32, labelSize * 1.2));
          }

          const metaSize = Math.max(9, Math.min(18, Math.sqrt(area) * 1.7));

          return (
            <div
              key={page.pageKey}
              className={`page-visits-heat-tile ${colorClasses[index % colorClasses.length]}${compactClass}${tinyClass}${inlineClass}${titlePriorityClass}`}
              style={{
                left: `${page.x}%`,
                top: `${page.y}%`,
                width: `${page.width}%`,
                height: `${page.height}%`,
                "--heat-label-size": `${labelSize}px`,
                "--heat-count-size": `${countSize}px`,
                "--heat-meta-size": `${metaSize}px`,
              }}
              onClick={() => this.showHeatMapPageUsers(page)}
              title={`${label} - ${page.visits} visits`}
            >
              <div className="page-visits-heat-main">
                <div className="page-visits-heat-label">{label}</div>
                <div className="page-visits-heat-count">{page.visits}</div>
              </div>
              <div className="page-visits-heat-meta">
                {percent}% · {page.userCount} {page.userCount === 1 ? "user" : "users"}
              </div>
            </div>
          );
        })}
        {this.state.isLoading && <div className="page-visits-heat-loading">Loading heat map...</div>}
      </div>
    );
  }

  renderHeatMapUserModal() {
    const page = this.state.selectedHeatMapPage;

    if (!page) {
      return null;
    }

    return (
      <div className="page-visits-modal-backdrop" onClick={this.closeHeatMapPageUsers}>
        <div className="page-visits-modal" onClick={(event) => event.stopPropagation()}>
          <div className="page-visits-modal-header">
            <div>
              <div className="page-visits-eyebrow">Page Visitors</div>
              <h2 className="page-visits-modal-title">{page.pageTitle}</h2>
              <div className="page-visits-subtitle">
                {page.visits} visits · {page.userCount} {page.userCount === 1 ? "user" : "users"}
              </div>
            </div>
            <button type="button" className="page-visits-modal-close" onClick={this.closeHeatMapPageUsers}>
              x
            </button>
          </div>

          <div className="page-visits-table-wrap">
            <table className="page-visits-table page-visits-modal-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Customer</th>
                  <th>Path</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {page.userRows.map((user) => (
                  <tr key={user.userKey}>
                    <td>
                      <div className="page-visits-user-name">{user.userName}</div>
                      <div className="page-visits-muted">{user.userKey}</div>
                    </td>
                    <td>{this.renderRoleBadge(user.role)}</td>
                    <td>{this.renderCustomer(user)}</td>
                    <td className="page-visits-modal-paths">
                      {user.pathRows.map((pathRow) => (
                        <div key={pathRow.path} className="page-visits-modal-path">
                          <span>{pathRow.path}</span>
                          {pathRow.visits > 1 && <span className="page-visits-modal-path-count">×{pathRow.visits}</span>}
                        </div>
                      ))}
                    </td>
                    <td>{user.visits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  renderUserDetail(visits, summaries) {
    const selectedUserId = this.props.match && this.props.match.params ? decodeURIComponent(this.props.match.params.userId || "") : "";
    const summary = summaries.find((item) => item.userKey === selectedUserId);
    const userVisits = summary ? summary.visits : visits.filter((visit) => this.getUserKey(visit) === selectedUserId);
    const displayName = summary ? summary.userName : selectedUserId;

    return (
      <div className="page-visits-page">
        <Helmet>
          <title>Page Visits - User Detail</title>
        </Helmet>
        <div className="page-visits-shell">
          <div className="page-visits-header">
            <div>
              <div className="page-visits-eyebrow">Admin Audit</div>
              <h1 className="page-visits-title">Page Visits</h1>
              <div className="page-visits-subtitle">{displayName} · {userVisits.length} visits in the last 24 hours</div>
            </div>
            <div className="page-visits-actions">
              {this.renderSearchInput()}
              <button type="button" className="page-visits-button page-visits-button-muted" onClick={this.showSummary}>
                Back
              </button>
            </div>
          </div>

          {this.state.error && <div className="page-visits-status page-visits-status-error">{this.state.error}</div>}

          <div className="page-visits-table-shell">
            <div className="page-visits-table-wrap">
              <table className="page-visits-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Page</th>
                    <th>Title</th>
                    <th>IP Address</th>
                    <th>User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {userVisits.map((visit) => (
                    <tr key={visit.id}>
                      <td>{this.renderDate(visit.visitStartedUtc)}</td>
                      <td>{this.renderCustomer(visit)}</td>
                      <td>{this.renderPageLink(visit)}</td>
                      <td>{visit.pageTitle}</td>
                      <td>{visit.ipAddress}</td>
                      <td className="page-visits-user-agent">{this.renderUserAgent(visit.userAgent)}</td>
                    </tr>
                  ))}
                  {!this.state.isLoading && userVisits.length === 0 && (
                    <tr>
                      <td className="page-visits-empty" colSpan="6">No page visits found for this user in the last 24 hours.</td>
                    </tr>
                  )}
                  {this.state.isLoading && (
                    <tr>
                      <td className="page-visits-empty" colSpan="6">Loading page visits...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderAllVisits(visits) {
    const highlightedUserKey = this.state.highlightedUserKey;

    return (
      <div className="page-visits-table-shell">
        <div className="page-visits-table-wrap">
          <table className="page-visits-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Role</th>
                <th>Customer</th>
                <th>IP Address</th>
                <th>User Agent</th>
                <th>Page</th>
                <th>Title</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {visits.map((visit) => {
                const userKey = this.getUserKey(visit);
                const isHighlighted = highlightedUserKey && highlightedUserKey === userKey;

                return (
                  <tr
                    key={visit.id}
                    className={isHighlighted ? "page-visits-row-highlighted" : ""}
                    onClick={() => this.toggleHighlightedUser(userKey)}
                  >
                    <td>{this.renderDate(visit.visitStartedUtc)}</td>
                    <td>
                      <div className="page-visits-user-name">{visit.userName}</div>
                      {visit.userId && <div className="page-visits-muted">{visit.userId}</div>}
                    </td>
                    <td>{this.renderRoleBadge(visit.role)}</td>
                    <td>{this.renderCustomer(visit)}</td>
                    <td>{visit.ipAddress}</td>
                    <td className="page-visits-user-agent">{this.renderUserAgent(visit.userAgent)}</td>
                    <td>{this.renderPath(visit)}</td>
                    <td>{visit.pageTitle}</td>
                    <td>
                      <button
                        type="button"
                        className="page-visits-button page-visits-button-primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          this.showUserDetail(userKey);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!this.state.isLoading && visits.length === 0 && (
                <tr>
                  <td className="page-visits-empty" colSpan="9">No page visits found in the last 24 hours.</td>
                </tr>
              )}
              {this.state.isLoading && (
                <tr>
                  <td className="page-visits-empty" colSpan="9">Loading page visits...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const allVisits = Array.isArray(this.state.visits) ? this.state.visits : [];
    const visits = this.getFilteredVisits(allVisits);
    const summaries = this.getUserSummaries(visits);
    const heatMapVisits = this.getHeatMapVisits(visits);
    const isHeatMapReport = this.props.heatMapOnly === true || this.state.showHeatMap;
    const pageTitle = isHeatMapReport
      ? "Page Visits - Heat Map"
      : this.state.showAllVisits
        ? "Page Visits - All Logins"
        : "Page Visits - User Summary";

    if (this.props.match && this.props.match.params && this.props.match.params.userId) {
      return this.renderUserDetail(visits, summaries);
    }

    return (
      <div className="page-visits-page">
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className="page-visits-shell">
          <div className="page-visits-header">
            <div>
              <div className="page-visits-eyebrow">Admin Audit</div>
              <h1 className="page-visits-title">{pageTitle}</h1>
              <div className="page-visits-subtitle">
                {isHeatMapReport
                  ? `${heatMapVisits.length} counted page visits in the last 24 hours · Page Visits report excluded`
                  : this.state.showAllVisits
                    ? `${visits.length} page visits in the last 24 hours`
                    : `${summaries.length} users active in the last 24 hours · ${visits.length} page visits`}
              </div>
            </div>
            <div className="page-visits-actions">
              {this.renderSearchInput()}
              {isHeatMapReport && (
                <button className="page-visits-button page-visits-button-muted" onClick={this.toggleHeatMapSystemAdmin} disabled={this.state.isLoading}>
                  {this.state.excludeSystemAdminFromHeatMap ? "Include SystemAdmin" : "Exclude SystemAdmin"}
                </button>
              )}
              {!isHeatMapReport && !this.state.showAllVisits && (
                <button className="page-visits-button page-visits-button-secondary" onClick={this.showAllLogins} disabled={this.state.isLoading}>
                  Show All Logins
                </button>
              )}
              {!isHeatMapReport && this.state.showAllVisits && (
                <button className="page-visits-button page-visits-button-secondary" onClick={this.showUserSummary} disabled={this.state.isLoading}>
                  Show User Summary
                </button>
              )}
              <button className="page-visits-button page-visits-button-primary" onClick={this.loadVisits} disabled={this.state.isLoading}>
                {this.state.isLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          <div className="page-visits-status page-visits-status-info">
            {isHeatMapReport
              ? `Showing the most popular pages by PageTitle within the last 24 hours. Page Visits report is excluded. ${this.state.excludeSystemAdminFromHeatMap ? "SystemAdmin visits are excluded." : "SystemAdmin visits are included."}`
              : this.state.showAllVisits
                ? "Showing all page visits recorded within the last 24 hours."
                : "Showing each user's page activity summary within the last 24 hours."}
          </div>

          {this.state.error && <div className="page-visits-status page-visits-status-error">{this.state.error}</div>}

          {isHeatMapReport ? this.renderHeatMap(visits) : this.state.showAllVisits ? this.renderAllVisits(visits) : (
            <div className="page-visits-table-shell">
              <div className="page-visits-table-wrap">
                <table className="page-visits-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Customer</th>
                      <th>Visits</th>
                      <th>Pages</th>
                      <th>Latest Visit</th>
                      <th>Latest Page</th>
                      <th>IP Address</th>
                      <th>User Agent</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaries.map((summary) => (
                      <tr key={summary.userKey}>
                        <td>
                          <div className="page-visits-user-name">{summary.userName}</div>
                          {summary.userId && <div className="page-visits-muted">{summary.userId}</div>}
                        </td>
                        <td>{this.renderRoleBadge(summary.role)}</td>
                        <td>{this.renderIpList(summary.customerList) || this.renderCustomer(summary)}</td>
                        <td>{summary.visits.length}</td>
                        <td>{summary.pageCount}</td>
                        <td>{this.renderDate(summary.latestVisit.visitStartedUtc)}</td>
                        <td>{this.renderPageLink(summary.latestVisit)}</td>
                        <td>{this.renderIpList(summary.ipList)}</td>
                        <td className="page-visits-user-agent">{this.renderUserAgent(summary.userAgent)}</td>
                        <td>
                          <button type="button" className="page-visits-button page-visits-button-primary" onClick={() => this.showUserDetail(summary.userKey)}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!this.state.isLoading && summaries.length === 0 && (
                      <tr>
                        <td className="page-visits-empty" colSpan="10">No users have page visits within the last 24 hours.</td>
                      </tr>
                    )}
                    {this.state.isLoading && (
                      <tr>
                        <td className="page-visits-empty" colSpan="10">Loading page visits...</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        {this.renderHeatMapUserModal()}
      </div>
    );
  }
}

export const PageVisitsHeatMap = (props) => <PageVisits {...props} heatMapOnly />;

export default PageVisits;
